import { Component, computed, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import {
  ApiResponse,
  Company,
  UserManagementData,
  UserProfile,
} from '../../../types/commons';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { UserManagementService } from '../../services/user-management/user-management.service';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  firstValueFrom,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { Paginator } from 'primeng/paginator';
import { OverlayModule } from '@angular/cdk/overlay';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InteractiveToastComponent } from '../common/interactive-toast/interactive-toast.component';
import { CreateUserRequest, UpdateUserRequest } from '../../../types/user';
import { ProfileService } from '../../services/profile/profile.service';
import { AuthService } from '../../services/auth/auth.service';
import { RuntimeConfigService } from '../../services/common/runtime-config.service';
import { TooltipModule } from 'primeng/tooltip';
import { OverflowTooltipDirective } from '../../directives/overflow-tooltip.directive';
import { emailValidator } from '../../app.validators';
import {
  ASYNC_VALIDATION_DEBOUNCE_MS,
  USER_SEARCH_INPUT_DEBOUNCE_MS,
} from '../../utils/debounce-times.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TOAST_DURATION_LONG_MS,
} from '../../utils/toast-durations.util';
import { DIAGNOSTIC_TOAST_LIFE_MS } from '../../utils/sheet-load-trace.util';
import { AccessDeniedNoticeService } from '../../services/common/access-denied-notice.service';
import { buildPageLoadFailureNotice } from '../../utils/page-load-failure.util';
import { PageLoadFailureToastGate } from '../../utils/page-load-failure-toast-gate.util';
import { AuthFailureNoticeService } from '../../services/auth/auth-failure-notice.service';
import { UserRole, parseUserRole } from '../../../types/user-role';
import { assignableRolesFor } from '../../utils/user-role.util';
import { RoleLabelPipe } from '../../utils/pipes/role-label.pipe';
import { HttpStatus } from '../../utils/http-status.util';
import {
  resolveUserManagementFailure,
  UserManagementAction,
} from '../../utils/user-management-error-codes.util';
import {
  CallerContext,
  canCreateUsers,
  canDeleteRow,
  canEditRow,
  canChangeCompany,
  canChangeRowRole,
  canEditEmail,
  companyOptionsFor,
  isCompanyFilterVisible,
} from './user-management-permissions.util';

interface UserFormRawValue {
  inputName: string | null;
  inputEmail: string | null;
  selectedCompany: string | null;
  selectedRole: string | null;
}

interface OriginalUserData {
  name: string;
  email: string;
  companyId: string;
  role: string;
}
@Component({
  selector: 'user-management',
  imports: [
    CommonModule,
    RoleLabelPipe,
    FormsModule,
    ReactiveFormsModule,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    OverflowTooltipDirective,
    SkeletonModule,
    Paginator,
    OverlayModule,
    Dialog,
    ConfirmDialogModule,
    InteractiveToastComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './user-management.component.html',
})
export class UserManagementComponent implements OnInit {
  /** List-filter options only. The create/edit picker is `rolesForm` below. */
  roles: string[] = [UserRole.Admin, UserRole.User];
  isTableLoading = signal<boolean>(false);
  openedOverlayId = signal<number | null>(null);
  companies = signal<Company[]>([]);
  selectedRole = signal<{ role: string }[]>([]);
  originalRole = signal<string | null>(null);
  fullData = signal<UserManagementData[]>([]);
  totalRecords = signal<number>(0);
  pendingUserChanges = signal<string[]>([]);
  isUserPending = signal<boolean>(false);
  selfData = signal<UserProfile | null>(null);
  isVisibleUser = signal<boolean>(false);
  isVisibleUserPending = signal<boolean>(false);
  isVisibleDeleteUser = signal<boolean>(false);
  isVisibleDeleteUserData = signal<UserManagementData | null>(null);
  isEditing = signal<string | null>(null);

  searchQuery = signal<string>('');
  selectedCompanyId = signal<string>('');
  selectedRoleName = signal<string>('');
  sortBy = signal<string>('');
  isDescending = signal<boolean>(false);

  userService = inject(UserManagementService);
  profileService = inject(ProfileService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  userManagementService = inject(UserManagementService);
  authService = inject(AuthService);
  authFailureNotice = inject(AuthFailureNoticeService);
  accessDeniedNotice = inject(AccessDeniedNoticeService);
  router = inject(Router);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private originalUserData: OriginalUserData | null = null;

  // Template-facing aliases of the pure permission rules, so the HTML reads
  // `canDeleteRow(caller(), ...)` and the rules stay unit-tested in one place.
  protected readonly canCreateUsers = canCreateUsers;
  protected readonly canDeleteRow = canDeleteRow;
  protected readonly canEditRow = canEditRow;
  protected readonly isCompanyFilterVisible = isCompanyFilterVisible;
  protected readonly parseUserRole = parseUserRole;

  /** Who is looking at the page: the signed-in role plus their own profile id. */
  readonly caller = computed<CallerContext>(() => ({
    role: this.authService.role(),
    userId: this.selfData()?.id ?? null,
  }));

  /**
   * Companies the create/edit picker offers. The list comes from
   * `GET /api/user/users` (`companies`, already scoped by role), never from the
   * profile, which carries only the caller's own company NAME (2026-10-20). A
   * SuperAdmin's profile has `company: null`, which is fine: only an Admin is
   * matched by name.
   */
  readonly companyOptions = computed<Company[]>(() =>
    companyOptionsFor(this.caller(), this.companies(), this.selfData()?.company ?? ''),
  );

  /**
   * Roles the create/edit picker offers. Editing a User or Admin row the caller
   * may re-role (`canChangeRowRole`: a SuperAdmin anywhere, an Admin in their own
   * company) offers exactly User and Admin. Creating offers what the caller may
   * assign (an Admin or SuperAdmin: User and Admin). Otherwise the row's current role is appended so
   * a locked picker still shows it instead of a blank.
   */
  readonly rolesForm = computed<string[]>(() => {
    const current = this.originalRole();
    const editingId = this.isEditing();
    // Editing a row whose role the caller may change: always User and Admin, so
    // an Admin can promote a User or downgrade an Admin in their own company.
    if (editingId && canChangeRowRole(this.caller(), { id: editingId, role: parseUserRole(current) })) {
      return [UserRole.User, UserRole.Admin];
    }
    // Creating: what the caller may assign (User and Admin; never SuperAdmin).
    const assignable: string[] = assignableRolesFor(this.authService.role());
    return editingId && current && !assignable.includes(current)
      ? [...assignable, current]
      : assignable;
  });

  // Caps this page's load-failure toast to one per class — see
  // PageLoadFailureToastGate's own docblock. ngOnInit fires fetchUsersList()
  // and fetchUSerProfile() together, so a single network outage used to fail
  // both at once and stack two identical "Connection lost..." toasts; every
  // call still reaches its own console record via buildPageLoadFailureNotice,
  // so neither failure loses traceability.
  private readonly loadFailureToastGate = new PageLoadFailureToastGate(
    undefined,
    // REFACTORED-DIAGNOSTIC-CODES-FLAG: silent page-level toasts in production.
    () => this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
  );

  isSubmitting: boolean = false;

  first = signal<number>(0);
  /**
   * Rows per page. Server-side: this value is sent to the backend as
   * `pageSize` on every fetch — it does not slice a client-side array.
   * Sourced from runtime config (`listingPageSize.userManagement`) so ops
   * can tune it per-deployment without a rebuild; committed default is 15.
   * (This listing was hardcoded to 9 before the key existed — the committed
   * template deliberately standardises it on the files listing's 15.)
   */
  rows = signal<number>(this.runtimeConfig.userManagementPageSize());

  private fetchUsersList(page?: number, pageSize?: number) {
    this.isTableLoading.set(true);
    this.userService
      .fetchUserList({
        page: page ?? 1,
        pageSize: pageSize ?? this.rows(),
        searchTerm: this.searchQuery(),
        companyId: this.selectedCompanyId(),
        roleName: this.selectedRoleName(),
        sortBy: this.sortBy(),
        isDescending: this.isDescending(),
      })
      .pipe(finalize(() => this.isTableLoading.set(false)))
      .subscribe({
        next: (data) => {
          if (!data) {
            this.fullData.set([]);
            this.totalRecords.set(0);
            return;
          }
          // Always replace, including with an EMPTY page: since 2026-10-08 the
          // backend answers a search/filter that matches nobody with 200 and an
          // empty page (it used to be 404). Updating only when `totalCount > 0`
          // left the previous rows and count on screen after a Company/Role
          // filter matched nobody, because those handlers do not clear first.
          this.fullData.set(data.users.items);
          this.totalRecords.set(data.users.totalCount);
          this.applyPendingUpdatesToFullData();
          if (data.companies.length > 0) {
            this.companies.set(data.companies);
          }
          if (data.pendingUserChanges && data.pendingUserChanges.length > 0) {
            this.pendingUserChanges.set(data.pendingUserChanges);
          }
        },
        error: (error: unknown) => {
          const notice = buildPageLoadFailureNotice('USER_MGMT_LISTING', error);
          if (notice.failure === 'PERM') {
            // Not allowed to see this page's data: leave for the Dashboard with the shell toast.
            this.accessDeniedNotice.denyPageAccess();
            return;
          }
          if (notice.silent) {
            this.authFailureNotice.stage(notice.summary);
          } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
            this.messageService.add({
              key: 'user',
              severity: 'error',
              summary: notice.summary,
              life: DIAGNOSTIC_TOAST_LIFE_MS,
            });
          }
        },
      });
  }

  private searchChanged = new Subject<string>();

  private fetchUSerProfile() {
    this.profileService
      .fetchProfileData()
      .pipe()
      .subscribe({
        next: (data) => {
          this.selfData.set(data);
        },
        error: (error: unknown) => {
          const notice = buildPageLoadFailureNotice('USER_MGMT_SELF', error);
          if (notice.failure === 'PERM') {
            // Not allowed to see this page's data: leave for the Dashboard with the shell toast.
            this.accessDeniedNotice.denyPageAccess();
            return;
          }
          if (notice.silent) {
            this.authFailureNotice.stage(notice.summary);
          } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
            this.messageService.add({
              key: 'user',
              severity: 'error',
              summary: notice.summary,
              life: DIAGNOSTIC_TOAST_LIFE_MS,
            });
          }
        },
      });
  }

  ngOnInit(): void {
    this.searchChanged.pipe(debounceTime(USER_SEARCH_INPUT_DEBOUNCE_MS)).subscribe((query) => {
      this.searchQuery.set(query);
      this.fullData.set([]);
      this.fetchUsersList();
    });

    this.fetchUsersList();
    this.fetchUSerProfile();
    this.setupEmailDuplicateCheck();
  }

  userForm = new FormGroup({
    inputName: new FormControl('', Validators.required),
    inputEmail: new FormControl('', [Validators.required, emailValidator()]),
    selectedCompany: new FormControl('', Validators.required),
    selectedRole: new FormControl('', Validators.required),
  });

  get inputName() {
    return this.userForm.get('inputName');
  }

  get inputEmail() {
    return this.userForm.get('inputEmail');
  }

  get selectedCompanyForm() {
    return this.userForm.get('selectedCompany');
  }

  get selectedRoleForm() {
    return this.userForm.get('selectedRole');
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    // Fall back to the configured size, not a stray literal — the paginator
    // always emits `rows`, but an unset fallback here would silently
    // repaginate the backend query at a size nothing else agrees on.
    const rows = event.rows ?? this.rows();

    this.first.set(first);
    this.rows.set(rows);

    const currentPage = Math.floor(first / rows) + 1;

    this.fetchUsersList(currentPage, rows);
  }

  isUserHasPendingChanges(userId: string) {
    if (this.pendingUserChanges().includes(userId)) {
      return true;
    }
    return false;
  }

  onTriggerOverlay(index: number): void {
    this.openedOverlayId.set(this.openedOverlayId() === index ? null : index);
  }

  showDialogUser(): void {
    this.isEditing.set(null);
    this.originalUserData = null;
    this.originalRole.set(null);
    this.enableRoleAndCompanyControls();
    this.userForm.reset();
    if (!canChangeCompany(this.caller())) {
      // An Admin can only create inside their own company: preset it and lock
      // it, but the payload still carries `companyId` (read via getRawValue()).
      this.selectedCompanyForm?.setValue(this.companyOptions()[0]?.id ?? '');
      this.selectedCompanyForm?.disable();
    }
    this.isVisibleUser.set(true);
  }

  closeDialogUser(): void {
    this.isVisibleUser.set(false);
  }

  /** False when the caller may neither edit nor delete this row, so the "..." menu would be empty. */
  protected hasRowActions(user: UserManagementData): boolean {
    const row = { id: user.id, role: parseUserRole(user.role) };
    return canEditRow(this.caller(), row) || canDeleteRow(this.caller(), row);
  }

  showDialogDeleteUser(user: UserManagementData): void {
    this.openedOverlayId.set(null);
    // One dialog for every role: deleting an Admin no longer touches their
    // conversions (P.O., 2026-10-20), so there is nothing extra to acknowledge.
    this.isVisibleDeleteUserData.set(user);
    this.isVisibleDeleteUser.set(true);
  }

  closeDialogUserPending(): void {
    this.isVisibleUserPending.set(false);
  }

  closeDialogDeleteUser(): void {
    // Row data stays until `onDeleteUserDialogHidden`: the template reads it
    // while PrimeNG fades the dialog out.
    this.isVisibleDeleteUser.set(false);
  }

  /** Bound to the Delete user dialog's `(onHide)`. Skips if it was re-opened meanwhile. */
  onDeleteUserDialogHidden(): void {
    if (this.isVisibleDeleteUser()) return;
    this.isVisibleDeleteUserData.set(null);
  }

  closeConfirmationDialog(): void {
    this.confirmationService.close();
  }

  editUser(user: UserManagementData) {
    this.openedOverlayId.set(null);
    this.isEditing.set(user.id);
    this.isVisibleUser.set(true);

    const matchingCompany = this.companies().find(
      (company) => company.name === user.company
    );

    this.originalUserData = {
      name: user.name,
      email: (user.email ?? '').trim(),
      companyId: matchingCompany?.id ?? '',
      role: user.role,
    };

    this.originalRole.set(user.role);
    this.enableRoleAndCompanyControls();
    this.userForm.patchValue({
      inputName: user.name,
      inputEmail: user.email,
      selectedCompany: matchingCompany?.id,
      selectedRole: user.role,
    });
    // Disabled controls are dropped by `.value`, so submit reads `getRawValue()`,
    // which still reports the unchanged values and therefore adds nothing to
    // the update diff.
    if (!canChangeCompany(this.caller())) {
      // An Admin never moves anyone to another company, themselves included.
      this.selectedCompanyForm?.disable();
    }
    if (!canEditEmail(this.caller(), { id: user.id, role: parseUserRole(user.role) })) {
      // Self, a fellow Admin, or an unknown role (P.O., 2026-10-27): shown with
      // the current value, never hidden. The server refuses a forged change.
      this.inputEmail?.disable();
    }
    if (!canChangeRowRole(this.caller(), { id: user.id, role: parseUserRole(user.role) })) {
      // Only a SuperAdmin row (never listed) or an unknown role lands here; an
      // Admin may promote or downgrade any User/Admin row of their own company
      // (AS-1387 / AS-1388).
      this.selectedRoleForm?.disable();
    }
  }

  /** The dialog is shared by create and edit, so start each open from enabled. */
  private enableRoleAndCompanyControls(): void {
    this.selectedCompanyForm?.enable();
    this.selectedRoleForm?.enable();
    this.inputEmail?.enable();
  }

  isButtonDisabled(): boolean {
    return (
      this.userForm.invalid ||
      this.userForm.pending ||
      this.isSubmitting ||
      this.userForm.pristine
    );
  }

  isSelf(): boolean {
    return this.selfData()?.id === this.isEditing();
  }

  min(first: number, second: number): number {
    return Math.min(first, second);
  }

  getPossessiveRoleLabel(name: string): string {
    if (this.isSelf()) {
      return 'your role';
    }

    const suffix = name.endsWith('s') ? "'" : "'s";
    return `<b>${name}${suffix}</b> role`;
  }

  // The original wording, kept as is (P.O., 2026-10-20, AS-1387 / AS-1388): another
  // Admin of the company can promote the account back, so the dialogs name
  // "another Admin" and deliberately do not mention a SuperAdmin.
  getRoleDowngradeRestoreText(): string {
    return this.isSelf()
      ? 'Require another Admin to restore your Admin status if needed.'
      : 'Require another Admin to restore the Admin status if needed.';
  }

  getRoleDowngradeLogoutText(): string {
    return this.isSelf()
      ? 'Log you out immediately for the changes to take place.'
      : 'Notify and log out the admin immediately for the changes to take place.';
  }

  getRoleDowngradeWarningText(): string {
    return this.isSelf()
      ? 'This change is irreversible unless another Admin upgrades your role again.'
      : 'This change is irreversible unless another Admin upgrades the role again.';
  }

  getRoleDowngradeButtonLabel(): string {
    return this.isSelf() ? 'Update and Log Out' : 'Update';
  }

  onSearchChange(query: string): void {
    this.searchChanged.next(query);
  }

  onCompanyChange(): void {
    this.fetchUsersList();
  }

  onRoleChange(): void {
    this.fetchUsersList();
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.selectedCompanyId.set('');
    this.selectedRoleName.set('');
    this.fetchUsersList();
  }

  onCancel() {
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
    this.userForm.updateValueAndValidity({ emitEvent: false });
  }

  onSort(event: { field: string; order: number }) {
    const columnName = event.field;
    const isDescending = event.order !== 1;

    if (this.sortBy() === columnName && this.isDescending() === isDescending) {
      return;
    }

    this.sortBy.set(columnName);
    this.isDescending.set(isDescending);
    this.fetchUsersList();
  }

  submitUserCreateUpdateForm() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const rawFormValue = this.userForm.getRawValue();

    const newRole = rawFormValue.selectedRole;
    const isDowngrading =
      this.isEditing() &&
      this.originalRole() === UserRole.Admin &&
      newRole === UserRole.User;

    if (this.isEditing()) {
      const submitEdit = () => {
        if (isDowngrading) {
          this.closeDialogUser();
          this.confirmationService.confirm({
            message: rawFormValue.inputName ?? '',
            accept: () => {
              this.proceedWithUpdate(rawFormValue, newRole);
            },
          });
        } else {
          this.proceedWithUpdate(rawFormValue, newRole);
        }
      };

      // A SuperAdmin moving a user to another company proceeds with no
      // confirmation (P.O., 2026-10-23: not in the user story). Only the
      // Admin->User downgrade above still asks.
      submitEdit();
    } else {
      this.isSubmitting = true;

      const credentials: CreateUserRequest = {
        name: rawFormValue.inputName ?? '',
        email: rawFormValue.inputEmail ?? '',
        companyId: rawFormValue.selectedCompany ?? '',
        role: rawFormValue.selectedRole ?? '',
      };

      this.userService
        .createUser(credentials)
        .pipe(
          tap({
            next: (response) => {
              if (response.status) {
                this.fetchUsersList();
                this.userForm.reset();
                this.isVisibleUser.set(false);
                this.messageService.add({
                  key: 'user',
                  severity: 'custom-success',
                  summary: 'The user has been successfully added.',
                  life: TOAST_DURATION_LONG_MS,
                });
              } else {
                this.presentFailure(response, 'create');
              }
            },
            finalize: () => {
              this.isSubmitting = false;
            },
          })
        )
        .subscribe();
    }
  }

  async proceedWithUpdate(
    rawFormValue: UserFormRawValue,
    newRole: string | null
  ) {
    this.isSubmitting = true;

    const id = this.isEditing() ?? '';

    if (await this.checkIfUserIsPending(id)) return;

    const credentials: UpdateUserRequest = {
      id: id,
    };

    if (this.originalUserData) {
      const trimmedName = (rawFormValue.inputName ?? '').trim();
      const trimmedEmail = (rawFormValue.inputEmail ?? '').trim();
      const companyId = rawFormValue.selectedCompany ?? '';
      const role = newRole ?? '';

      if (trimmedName !== this.originalUserData.name) {
        credentials.name = trimmedName;
      }

      if (!this.inputEmail?.disabled && trimmedEmail !== this.originalUserData.email) {
        credentials.email = trimmedEmail;
      }

      if (companyId !== this.originalUserData.companyId) {
        credentials.companyId = companyId;
      }

      if (role !== this.originalUserData.role) {
        credentials.role = role;
      }
    } else {
      credentials.name = rawFormValue.inputName ?? '';
      credentials.email = rawFormValue.inputEmail ?? '';
      credentials.companyId = rawFormValue.selectedCompany ?? '';
      credentials.role = newRole ?? '';
    }

    this.userService
      .updateUser(credentials)
      .pipe(
        tap({
          next: (response) => {
            if (response.status) {
              // Check if this is a self-downgrade (Admin to User)
              const isSelfDowngrade =
                this.isSelf() &&
                this.originalRole() === UserRole.Admin &&
                newRole === UserRole.User;

              // Optimistically update local data immediately
              this.updateLocalUserData(id, rawFormValue, newRole);

              this.fetchUsersList();
              this.userForm.reset();
              this.isVisibleUser.set(false);
              this.isEditing.set(null);
              this.originalUserData = null;
              this.isDescending.set(true);
              this.sortBy.set('lastmodified');
              this.first.set(0);
              this.messageService.add({
                key: 'user',
                severity: 'custom-success',
                summary: 'The user has been successfully updated.',
                life: TOAST_DURATION_LONG_MS,
              });

              // An Admin who downgraded THEMSELVES (AS-1387 / AS-1388) is signed
              // out. The redirect runs on BOTH arms of logout(), as on My Profile:
              // if sign-out fails, the account still holds a role the server has
              // already changed, so staying on this page would strand them.
              if (isSelfDowngrade) {
                this.authService
                  .logout()
                  .pipe(finalize(() => this.router.navigate(['/auth/login'])))
                  .subscribe({ error: () => {} });
              }
            } else {
              // A refused move means the user or the company is gone, so
              // refetch: the table must never be left stale.
              this.presentFailure(response, 'update', credentials.companyId !== undefined);
            }
          },
          finalize: () => {
            this.isSubmitting = false;
          },
        })
      )
      .subscribe();
  }

  /**
   * The one place a failed create/update/delete is shown. The backend `errorCode`
   * (via `resolveUserManagementFailure`) picks the toast text, including
   * "temporarily unavailable" for the SuperAdmin audit 503. A failure does not
   * close the create/edit form, so the user keeps what they typed. A 404 is the
   * exception: the user is gone, so the dialogs close and the list is refetched.
   */
  private presentFailure(
    response: ApiResponse,
    action: UserManagementAction,
    refreshList = false,
  ): void {
    const failure = resolveUserManagementFailure(response, action, {
      callerRole: this.authService.role(),
    });

    if (failure.presentation === 'emailTaken') {
      // Same field error the live availability check paints; the dialog stays
      // open and Save stays disabled until the email is changed again.
      this.inputEmail?.markAsTouched();
      this.setControlError(this.inputEmail as AbstractControl, 'emailTaken', true);
      return;
    }

    this.messageService.add({
      key: 'user',
      severity: 'custom-error',
      summary: failure.message,
      life: TOAST_DURATION_LONG_MS,
    });

    const isGone = failure.statusCode === HttpStatus.NotFound;
    if (isGone) {
      this.closeDialogUser();
      this.closeDialogDeleteUser();
    }
    if (isGone || refreshList) {
      this.fetchUsersList();
    }
  }

  private updateLocalUserData(
    userId: string,
    rawFormValue: UserFormRawValue,
    newRole: string | null
  ): void {
    const companyName =
      this.companies().find((company) => company.id === rawFormValue.selectedCompany)
        ?.name ?? '';

    const pendingUpdate = {
      id: userId,
      name: rawFormValue.inputName?.trim() ?? '',
      email: rawFormValue.inputEmail?.trim() ?? '',
      company: companyName,
      role: newRole ?? '',
      timestamp: Date.now(),
    };

    this.savePendingUpdate(pendingUpdate);
    this.applyPendingUpdatesToFullData();
  }

  private savePendingUpdate(update: {
    id: string;
    name: string;
    email: string;
    company: string;
    role: string;
    timestamp: number;
  }): void {
    const pending = this.getPendingUpdates();
    const filtered = pending.filter((pendingUpdate) => pendingUpdate.id !== update.id);
    filtered.push(update);
    localStorage.setItem(
      'user-management-pending-updates',
      JSON.stringify(filtered)
    );
  }

  private getPendingUpdates(): Array<{
    id: string;
    name: string;
    email: string;
    company: string;
    role: string;
    timestamp: number;
  }> {
    const stored = localStorage.getItem('user-management-pending-updates');
    if (!stored) return [];

    const pending = JSON.parse(stored);
    const now = Date.now();
    const validPending = pending.filter(
      (pendingUpdate: { timestamp: number }) => now - pendingUpdate.timestamp < 15000
    );

    if (validPending.length !== pending.length) {
      localStorage.setItem(
        'user-management-pending-updates',
        JSON.stringify(validPending)
      );
    }

    return validPending;
  }

  private applyPendingUpdatesToFullData(): void {
    const pendingUpdates = this.getPendingUpdates();
    if (pendingUpdates.length === 0) return;

    const currentData = this.fullData();
    const updatedData = currentData.map((user) => {
      const pending = pendingUpdates.find((pendingUpdate) => pendingUpdate.id === user.id);
      if (pending) {
        return {
          ...user,
          name: pending.name || user.name,
          email: pending.email || user.email,
          company: pending.company || user.company,
          role: parseUserRole(pending.role) ?? user.role,
          lastUpdated: new Date(pending.timestamp),
        };
      }
      return user;
    });
    this.fullData.set(updatedData);
  }

  async submitDeleteUser() {
    if (!this.isVisibleDeleteUserData()) {
      return;
    }
    this.isSubmitting = true;

    const id = this.isVisibleDeleteUserData()?.id ?? '';

    if (await this.checkIfUserIsPending(id)) return;

    const credentials = {
      id: id,
    };

    this.userService
      .deleteUser(credentials)
      .pipe(
        tap({
          next: (response) => {
            if (response.status) {
              this.fetchUsersList();
              this.isVisibleDeleteUser.set(false);
              this.messageService.add({
                key: 'user',
                severity: 'custom-success',
                summary: 'The user has been successfully deleted.',
                life: TOAST_DURATION_LONG_MS,
              });
            } else {
              this.presentFailure(response, 'delete');
            }
          },
          finalize: () => {
            this.isSubmitting = false;
          },
        })
      )
      .subscribe();
  }

  async checkIfUserIsPending(userId: string): Promise<boolean> {
    // A transient failure here must fail OPEN (treat as "no pending changes")
    // rather than reject `firstValueFrom` and leave `proceedWithUpdate` /
    // `submitDeleteUser` stuck with `isSubmitting` permanently `true`. This is a
    // background completeness-check, not a primary page load, so it does not
    // toast — the write operation this check gates already reports its own
    // failures via its own `tap`/`finalize` handling.
    const response = await firstValueFrom(
      this.userService.fetchUserList().pipe(catchError(() => of(null)))
    );

    if (response) {
      this.pendingUserChanges.set(response.pendingUserChanges || []);
      const isUserPending = this.isUserHasPendingChanges(userId);

      if (!isUserPending) return false;

      this.closeDialogUser();
      this.isEditing.set(null);
      this.closeDialogDeleteUser();
      this.isSubmitting = false;
      this.isVisibleUserPending.set(true);

      return true;
    }
    return false;
  }

  private setupEmailDuplicateCheck() {
    const emailCtrl = this.userForm.get('inputEmail') as FormControl;

    emailCtrl.valueChanges
      .pipe(
        map((email) => (email ?? '').toString().toLowerCase()),

        tap((email) => {
          const orig = (this.originalUserData?.email ?? '')
            .trim()
            .toLowerCase();
          if (email === orig) {
            this.setControlError(emailCtrl, 'emailTaken', false);
          } else if (
            !emailCtrl.disabled &&
            !emailCtrl.hasError('required') &&
            !emailCtrl.hasError('invalidEmailSyntax') &&
            !emailCtrl.hasError('email')
          ) {
            emailCtrl.markAsPending();
          }
        }),

        filter(
          () =>
            !emailCtrl.disabled &&
            !emailCtrl.hasError('required') &&
            !emailCtrl.hasError('invalidEmailSyntax') &&
            !emailCtrl.hasError('email')
        ),
        debounceTime(ASYNC_VALIDATION_DEBOUNCE_MS),

        filter(
          (email) => email !== (this.originalUserData?.email ?? '').trim().toLowerCase()
        ),

        switchMap((email) => {
          return this.userManagementService.checkEmailAvailability(email).pipe(
            // service returns: true => TAKEN, false => AVAILABLE
            catchError(() => of(false))
          );
        }),

        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((taken) => {
        this.setControlError(emailCtrl, 'emailTaken', taken);
      });
  }

  private setControlError(
    ctrl: AbstractControl,
    key: string,
    present: boolean
  ) {
    const errs = { ...(ctrl.errors ?? {}) };
    if (present) errs[key] = true;
    else delete errs[key];
    ctrl.setErrors(Object.keys(errs).length ? errs : null);
  }
}
